export const TENANT_PARAM = 'tenant_id'

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The tenant id is interpolated straight into API paths (`/v1/TenantProduct/{tenantId}`),
 * so it is validated as a GUID before it is ever used. Anything else is treated as
 * "no tenant" — a `../` or an injected path segment must never reach the backend.
 */
export function parseTenantId(raw?: string | string[] | null): string | null {
  const value = Array.isArray(raw) ? raw[0] : raw
  if (!value) return null
  const trimmed = value.trim()
  return GUID_RE.test(trimmed) ? trimmed : null
}

/**
 * Builds an in-funnel URL with the tenant always attached. Every internal link
 * goes through this — the funnel is URL-as-state and each step must stay
 * shareable and refresh-safe on its own.
 */
export function tenantUrl(
  path: string,
  tenantId: string,
  params: Record<string, string | number | null | undefined> = {},
) {
  const search = new URLSearchParams({ [TENANT_PARAM]: tenantId })
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') search.set(key, String(value))
  }
  return `${path}?${search.toString()}`
}

/**
 * All tenants share one origin under the query-param model, so browser storage
 * MUST be namespaced or one tenant's buyer details leak into another's form.
 */
export function tenantStorageKey(tenantId: string, key: string) {
  return `shop:${tenantId}:${key}`
}

/**
 * The left-most label of a hostname, e.g. "acme.storims.com" -> "acme". Used by
 * resolve-subdomain.ts to turn a subdomain hostname into the slug the API resolves
 * against. Kept here (rather than duplicated) since it's conceptually the same
 * "identify the tenant" concern as `parseTenantId` — but stays a plain string
 * function so this file never picks up a `fetch` dependency.
 */
export function parseSubdomainSlug(hostname: string): string {
  return hostname.split('.')[0]
}
